import { pushToast } from '@/notifications';

export type UiPrefsState = {
  statsOverlaySeen?: boolean;
  reducedMotionOverride?: boolean | null;
  soundEnabled?: boolean;
  devLocaleUnlock?: boolean;
  personalStatsLastSeenLongest?: number;
  assistEnabled?: boolean;            // default false
  hardModeEnabled?: boolean;          // default false — must survive reload (§3.5)
  assistRevealDate?: string | null;   // YYYY-MM-DD the single reveal was consumed; default null
};

export interface UiPrefs {
  getSoundEnabled(): boolean;
  setSoundEnabled(v: boolean): void;
  getReducedMotionOverride(): boolean | null;
  setReducedMotionOverride(v: boolean | null): void;
  getReducedMotion(): boolean;
  getStatsOverlaySeen(): boolean;
  setStatsOverlaySeen(v: boolean): void;
  getPersonalStatsLastSeenLongest(): number;
  setPersonalStatsLastSeenLongest(v: number): void;
  getDevLocaleUnlock(): boolean;
  getAssistEnabled(): boolean;
  setAssistEnabled(v: boolean): void;
  getHardModeEnabled(): boolean;
  setHardModeEnabled(v: boolean): void;
  getAssistRevealDate(): string | null;
  setAssistRevealDate(v: string | null): void;
  resetAll(): void;
}

const KEY = 'napi11es:ui:v1';

function osPrefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  catch { return false; }
}

export function createUiPrefs(storage: Storage): UiPrefs {
  let state: UiPrefsState = load();

  function load(): UiPrefsState {
    try {
      const raw = storage.getItem(KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch { return {}; }
  }
  function save(): void {
    try { storage.setItem(KEY, JSON.stringify(state)); }
    catch { pushToast('A helyi tárhely nem érhető el — adataid csak munkamenet erejéig maradnak meg', 'error'); }
  }

  return {
    getSoundEnabled: () => state.soundEnabled ?? false,
    setSoundEnabled: (v) => { state = { ...state, soundEnabled: v }; save(); },
    getReducedMotionOverride: () => state.reducedMotionOverride ?? null,
    setReducedMotionOverride: (v) => { state = { ...state, reducedMotionOverride: v }; save(); },
    getReducedMotion: () => {
      const ovr = state.reducedMotionOverride;
      if (ovr === true || ovr === false) return ovr;
      return osPrefersReducedMotion();
    },
    getStatsOverlaySeen: () => state.statsOverlaySeen === true,
    setStatsOverlaySeen: (v) => { state = { ...state, statsOverlaySeen: v }; save(); },
    getPersonalStatsLastSeenLongest: () => state.personalStatsLastSeenLongest ?? 0,
    setPersonalStatsLastSeenLongest: (v) => { state = { ...state, personalStatsLastSeenLongest: v }; save(); },
    getDevLocaleUnlock: () => state.devLocaleUnlock === true,
    getAssistEnabled: () => state.assistEnabled ?? false,
    setAssistEnabled: (v) => { state = { ...state, assistEnabled: v }; save(); },
    getHardModeEnabled: () => state.hardModeEnabled ?? false,
    setHardModeEnabled: (v) => { state = { ...state, hardModeEnabled: v }; save(); },
    getAssistRevealDate: () => state.assistRevealDate ?? null,
    setAssistRevealDate: (v) => { state = { ...state, assistRevealDate: v }; save(); },
    resetAll: () => {
      state = {};
      try { storage.removeItem(KEY); }
      catch { pushToast('Törlés sikertelen', 'error'); }
    },
  };
}
